/**
 * Map Size Presets
 *
 * Zoom-dependent rendering is calibrated for z16-z20 (small venues).
 * Map size presets apply a zoom offset so larger venues (z12-z20)
 * get correct POI tiers, prop density, icon scaling, and shadows.
 */

import type { ThemeConfig, PoiMinZoom } from "./types";

export type MapSize = "small" | "medium" | "large";

export interface MapSizePreset {
  offset: number;
  visitorMinZoom: number;
  visitorMaxZoom: number;
  fitBoundsMaxZoom: number;
}

export const MAP_SIZE_PRESETS: Record<MapSize, MapSizePreset> = {
  small: { offset: 0, visitorMinZoom: 16, visitorMaxZoom: 20, fitBoundsMaxZoom: 17 },
  medium: { offset: -2, visitorMinZoom: 14, visitorMaxZoom: 20, fitBoundsMaxZoom: 15 },
  large: { offset: -4, visitorMinZoom: 12, visitorMaxZoom: 20, fitBoundsMaxZoom: 13 },
};

/** Clamp zoom to valid MapLibre range */
function clampZoom(z: number): number {
  return Math.max(0, Math.min(24, z));
}

/**
 * Get zoom offset for a map size. Accepts numeric override for future custom escape hatch.
 */
export function getZoomOffset(size: MapSize | number): number {
  if (typeof size === "number") return size;
  return MAP_SIZE_PRESETS[size].offset;
}

/** Apply offset to a base zoom level, clamped 0-24 */
export function offsetZoom(baseZoom: number, offset: number): number {
  return clampZoom(baseZoom + offset);
}

/** Get POI min zoom thresholds shifted by map size */
export function getPoiMinZoomForSize(size: MapSize | number): PoiMinZoom {
  const offset = getZoomOffset(size);
  return {
    1: clampZoom(16 + offset),
    2: clampZoom(17 + offset),
    3: clampZoom(18 + offset),
  };
}

/**
 * Apply map size offset to a theme config.
 * Shifts poiMinZoom, strokeWidthZoom, and prop minZoom values.
 * Returns a new ThemeConfig (does not mutate input).
 */
export function applyMapSizeToTheme(theme: ThemeConfig, size: MapSize | number): ThemeConfig {
  const offset = getZoomOffset(size);
  if (offset === 0) return theme;

  const shifted = { ...theme };

  // Shift POI min zoom tiers
  shifted.poiMinZoom = getPoiMinZoomForSize(size);

  // Shift strokeWidthZoom on layers
  const shiftedLayers = { ...theme.layers };
  for (const [key, style] of Object.entries(shiftedLayers)) {
    if (style.strokeWidthZoom) {
      shiftedLayers[key as keyof typeof shiftedLayers] = {
        ...style,
        strokeWidthZoom: {
          ...style.strokeWidthZoom,
          minZoom: clampZoom(style.strokeWidthZoom.minZoom + offset),
          maxZoom: clampZoom(style.strokeWidthZoom.maxZoom + offset),
        },
      };
    }
  }
  shifted.layers = shiftedLayers;

  // Shift prop minZoom values
  if (theme.props) {
    const shiftedProps = { ...theme.props };
    for (const [key, prop] of Object.entries(shiftedProps)) {
      if (prop.minZoom !== undefined) {
        shiftedProps[key] = {
          ...prop,
          minZoom: clampZoom(prop.minZoom + offset),
        };
      }
    }
    shifted.props = shiftedProps;
  }

  return shifted;
}
